/**
 * Page Level Detection and Performance Optimization Utilities
 * Implements hierarchical performance strategy based on URL depth
 */

// Network Information API type
interface NetworkInformation {
  saveData?: boolean;
  effectiveType?: 'slow-2g' | '2g' | '3g' | '4g';
  downlink?: number;
  rtt?: number;
}

export enum PageLevel {
  LEVEL_0 = 0, // Homepage - VRM 3D + GSAP全機能
  LEVEL_1 = 1, // Main sections - CTA + GSAP全機能
  LEVEL_2 = 2, // Category hubs - ハブ + GSAP最適化
  LEVEL_3 = 3, // Content pages - GSAP読書体験
}

export interface PerformanceTier {
  level: PageLevel;
  maxLCP: number; // in seconds
  animations: {
    gsap: boolean;
    framerMotion: boolean;
    cssTransitions: boolean;
    maxDuration: number; // in ms
  };
  features: {
    parallax: boolean;
    headerTransparency: boolean;
  };
}

/**
 * Get the page level based on URL path
 */
export function getPageLevel(pathname: string): PageLevel {
  const segments = pathname.split('/').filter(Boolean);
  
  if (segments.length === 0) {
    return PageLevel.LEVEL_0; // Homepage
  }
  
  if (segments.length === 1) {
    return PageLevel.LEVEL_1; // Main sections
  }
  
  if (segments.length === 2) {
    return PageLevel.LEVEL_2; // Category hubs
  }
  
  return PageLevel.LEVEL_3; // Content pages
}

/**
 * Get performance tier configuration based on page level
 */
export function getPerformanceTier(level: PageLevel): PerformanceTier {
  switch (level) {
    case PageLevel.LEVEL_0:
      return {
        level: PageLevel.LEVEL_0,
        maxLCP: 2.5,
        animations: {
          gsap: true,
          framerMotion: true,
          cssTransitions: true,
          maxDuration: 1000,
        },
        features: {
          parallax: true,
          headerTransparency: true,
        },
      };
      
    case PageLevel.LEVEL_1:
      return {
        level: PageLevel.LEVEL_1,
        maxLCP: 2.5,
        animations: {
          gsap: true,
          framerMotion: false,
          cssTransitions: true,
          maxDuration: 800,
        },
        features: {
          parallax: true,
          headerTransparency: true,
        },
      };
      
    case PageLevel.LEVEL_2:
      return {
        level: PageLevel.LEVEL_2,
        maxLCP: 2.5,
        animations: {
          gsap: true,
          framerMotion: false,
          cssTransitions: true,
          maxDuration: 600,
        },
        features: {
          parallax: false,
          headerTransparency: false,
        },
      };
      
    case PageLevel.LEVEL_3:
    default:
      return {
        level: PageLevel.LEVEL_3,
        maxLCP: 2.5,
        animations: {
          gsap: true,
          framerMotion: false,
          cssTransitions: true,
          maxDuration: 400,
        },
        features: {
          parallax: true,
          headerTransparency: false,
        },
      };
  }
}

/**
 * Dynamic import utilities for performance-critical libraries
 */
export const dynamicImports = {
  gsap: async () => {
    const [gsap, ScrollTrigger] = await Promise.all([
      import('gsap').then(m => m.gsap),
      import('gsap/ScrollTrigger').then(m => m.ScrollTrigger),
    ]);
    gsap.registerPlugin(ScrollTrigger);
    return { gsap, ScrollTrigger };
  },
  
  framerMotion: async () => {
    const { motion, AnimatePresence } = await import('framer-motion');
    return { motion, AnimatePresence };
  },
  
};

/**
 * Check if animations should be loaded based on user preferences
 */
export function shouldLoadAnimations(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  
  // Check for reduced motion preference
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReducedMotion) {
    return false;
  }
  
  // Check for save data mode
  const connection = (navigator as Navigator & { connection?: NetworkInformation }).connection;
  if (connection?.saveData) {
    return false;
  }
  
  // Check for slow connection
  if (connection?.effectiveType && ['slow-2g', '2g'].includes(connection.effectiveType)) {
    return false;
  }
  
  return true;
}

/**
 * Preload critical resources based on page level
 */
export function preloadResourcesForLevel(level: PageLevel): void {
  if (typeof window === 'undefined') {
    return;
  }
  
  const tier = getPerformanceTier(level);
  
  // Preload fonts
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'font';
  link.type = 'font/woff2';
  link.href = '/fonts/primary-font.woff2';
  link.crossOrigin = 'anonymous';
  document.head.appendChild(link);
  
  // Preload critical images based on level
  if (level === PageLevel.LEVEL_0) {
    // Preload hero images
    const heroImg = document.createElement('link');
    heroImg.rel = 'preload';
    heroImg.as = 'image';
    heroImg.href = '/images/hero-bg.webp';
    document.head.appendChild(heroImg);
  }
  
  // Prefetch next level resources
  if (tier.animations.gsap) {
    const gsapLink = document.createElement('link');
    gsapLink.rel = 'modulepreload';
    gsapLink.href = '/_next/static/chunks/gsap.js';
    document.head.appendChild(gsapLink);
  }
}